/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useRecordingForm.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing Recording Management form state and validation.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useRef, useMemo } from 'react';
import { VoiceRecording } from '../types';
import type { AiProfile } from '../../ai-profiles/api/aiProfilesApi';

export type RecordingMode = 'upload' | 'tts' | 'system';

export interface FormSnapshot {
  form: {
    name: string;
    description: string;
    category: string;
    language: string;
    status: string;
  };
  mode: RecordingMode;
  aiProfileId: number | null;
  ttsText: string;
  voiceGuideId: number | null;
  voiceGuideFilePath: string | null;
}

/**
 * Hook for managing recording form state
 */
export function useRecordingForm() {
  const [formData, setFormData] = useState<Partial<VoiceRecording>>({
    name: '',
    description: '',
    category: 'announcement',
    language: 'en',
    status: 'active',
  });

  const [recordingMode, setRecordingMode] = useState<RecordingMode>('upload');
  const [selectedAiProfile, setSelectedAiProfile] = useState<AiProfile | null>(null);
  const [ttsText, setTtsText] = useState('');
  // In `VoiceRecording` we don't have a direct strict voice_guide_id column yet,
  // we will inject it into formData for submission.
  const [voiceGuideId, setVoiceGuideId] = useState<number | null>(null);
  const [voiceGuideFilePath, setVoiceGuideFilePath] = useState<string | null>(null);

  const initialSnapshotRef = useRef<FormSnapshot | null>(null);

  const buildSnapshot = useCallback((): FormSnapshot => {
    return {
      form: {
        name: (formData.name ?? '').trim(),
        description: (formData.description ?? '').trim(),
        category: formData.category ?? 'announcement',
        language: formData.language ?? 'en',
        status: formData.status ?? 'active',
      },
      mode: recordingMode,
      aiProfileId: selectedAiProfile?.id ?? null,
      ttsText: ttsText.trim(),
      voiceGuideId: (formData as any).voice_guide_id ?? voiceGuideId,
      voiceGuideFilePath: (formData as any).voice_guide_file_path ?? voiceGuideFilePath,
    };
  }, [formData, recordingMode, selectedAiProfile, ttsText, voiceGuideId, voiceGuideFilePath]);

  const hasChanges = useMemo(() => {
    const snapshot = initialSnapshotRef.current;
    if (!snapshot) {
      return true; // New form always has changes
    }
    const current = buildSnapshot();
    return JSON.stringify(snapshot) !== JSON.stringify(current);
  }, [buildSnapshot]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = useCallback((field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      category: 'announcement',
      language: 'en',
      status: 'active',
    });
    setRecordingMode('upload');
    setTtsText('');
    setSelectedAiProfile(null);
  }, []);

  return {
    formData,
    setFormData,
    recordingMode,
    setRecordingMode,
    selectedAiProfile,
    setSelectedAiProfile,
    ttsText,
    setTtsText,
    buildSnapshot,
    hasChanges,
    handleInputChange,
    resetForm,
    initialSnapshotRef,
    voiceGuideId,
    setVoiceGuideId,
    voiceGuideFilePath,
    setVoiceGuideFilePath,
  };
}

